// UCSF-3519: browser timezone helpers for auto-detection at sign-in and the profile Timezone field.

export interface ITimezoneOption {
  id: string;
  name: string;
  offsetMinutes: number;
}

/** The IANA zone the browser reports, or null when it reports none. */
export function getBrowserTimezone(): string | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return zone || null;
  } catch {
    return null;
  }
}

/** Current UTC offset of a zone, in minutes (e.g. 180 for Europe/Moscow). */
export function getTimezoneOffsetMinutes(timeZone: string, date: Date = new Date()): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric'
  }).formatToParts(date);
  const part = (type: string) => Number(parts.find(p => p.type === type)?.value);
  const asUtc = Date.UTC(part('year'), part('month') - 1, part('day'), part('hour'), part('minute'));
  const utcMinute = Math.floor(date.getTime() / 60000) * 60000;

  return Math.round((asUtc - utcMinute) / 60000);
}

/** "UTC+03:00" style label for an offset in minutes. */
export function formatUtcOffset(offsetMinutes: number): string {
  const sign = offsetMinutes < 0 ? '-' : '+';
  const abs = Math.abs(offsetMinutes);
  const hours = String(Math.floor(abs / 60)).padStart(2, '0');
  const minutes = String(abs % 60).padStart(2, '0');

  return `UTC${sign}${hours}:${minutes}`;
}

function getSupportedTimezones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };

  try {
    return intl.supportedValuesOf ? intl.supportedValuesOf('timeZone') : [];
  } catch {
    return [];
  }
}

/**
 * Zone options labelled "(UTC+03:00) Europe/Moscow", sorted by offset, then name. The stored value is
 * always included, so it stays selectable in a browser without Intl.supportedValuesOf.
 */
export function getTimezoneOptions(currentValue?: string | null, date: Date = new Date()): ITimezoneOption[] {
  const zones = new Set(getSupportedTimezones());

  if (currentValue) {
    zones.add(currentValue);
  }

  const options: ITimezoneOption[] = [];

  zones.forEach(zone => {
    try {
      const offsetMinutes = getTimezoneOffsetMinutes(zone, date);

      options.push({id: zone, name: `(${formatUtcOffset(offsetMinutes)}) ${zone}`, offsetMinutes});
    } catch {
      // A zone this browser cannot format is left out of the list.
    }
  });

  return options.sort((a, b) => a.offsetMinutes - b.offsetMinutes || a.id.localeCompare(b.id));
}
